import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import {
  addOfflineOperation,
  updateConnectionStatus,
  resetOfflineOperation,
  markOperationExecuted,
  markOperationExecutedWithError,
  syncData,
  addIdMapping,
  clearIdMappings,
  removeOfflineOperation,
  updateOfflineOperation,
} from './../actions/offline.action';
import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import { OfflineOperation, IdMapping } from 'src/app/models/offlineOperation.model';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

export const OFFLINE_OPERATIONS = 'OFFLINE_OPERATIONS';

export interface ConnectionState extends EntityState<OfflineOperation> {
  isOnline: boolean;
  connectionQuality: 'good' | 'poor' | 'offline';  // Add connection quality tracking
  isSyncOnProgress: boolean;
  isSyncDone: boolean;
  nextOfflineIndex: number;
  failedSyncOperations: Array<any>;
  idMappings: { [tempId: string]: IdMapping }; // Store mapping ID
}

export const offlineAdapter: EntityAdapter<OfflineOperation> =
  createEntityAdapter<OfflineOperation>({
    selectId: (operation) => operation.id,
  });

export const initialState: ConnectionState = offlineAdapter.getInitialState({
  isOnline: true,
  connectionQuality: 'good',  // Initialize with good quality
  nextOfflineIndex: uuidv4(),
  failedSyncOperations: [],
  isSyncOnProgress: false,
  isSyncDone: true,
  idMappings: {}, // Initialize ID mapping
});

export const connectionFeatureSelector =
  createFeatureSelector<ConnectionState>('connection');

export const connectionStatus = createSelector(
  connectionFeatureSelector,
  (state) => state.isOnline
);
export const isAppSyncData = createSelector(connectionFeatureSelector, (state) =>
  Array.from(Object.values(state.entities)).length ? true : false
);
export const selectIsSyncInProgress = createSelector(
  connectionFeatureSelector,
  (state) => state.isSyncOnProgress
);
export const selectIsSyncDone = createSelector(
  connectionFeatureSelector,
  (state) => state.isSyncDone
);
export const nextOfflineIndex = createSelector(
  connectionFeatureSelector,
  (state) => 'offline-' + state.nextOfflineIndex
);
export const offlineOperations = createSelector(
  connectionFeatureSelector,
  (state) => state.entities
);

export const connectionReducer = createReducer(
  initialState,
  on(addOfflineOperation, (state, action) => {

    return offlineAdapter.addOne(
      {
        ...action.operation,
        id: state.nextOfflineIndex,
        synced: false,
        priority: action.operation.priority || 'normal',
        tempId: action.operation.tempId ? action.operation.tempId : undefined,
      },
      {
        ...state,
        nextOfflineIndex: uuidv4(),
      }
    );
  }),
  on(syncData, (state, action) => {
    return {
      ...state,
      isSyncOnProgress: Array.from(Object.values(state.entities)).length != 0,
      isSyncDone: Array.from(Object.values(state.entities)).length == 0,
    };
  }),
  on(markOperationExecuted, (state, action: any) => {
    return offlineAdapter.removeOne(action.id, {
      ...state,
      isSyncOnProgress: Array.from(Object.values(state.entities)).length > 1,
      isSyncDone: Array.from(Object.values(state.entities)).length == 0,
      idMappings: Array.from(Object.values(state.entities)).length == 1 ? {} : state.idMappings
    });
  }),
  on(removeOfflineOperation, (state, action: any) => {
    return offlineAdapter.removeOne(action.id, {
      ...state
    });
  }),
  on(updateOfflineOperation, (state, action) => {
    return offlineAdapter.updateOne({ id: action.id, changes: { formDataObject: action.updatedFormDataObject } }, state);
  }),
  on(markOperationExecutedWithError, (state, action) => {
    let failedSyncOperations = cloneDeep(state.failedSyncOperations);
    failedSyncOperations.push(action.error);
    return offlineAdapter.removeOne(action.id, {
      ...state,
      failedSyncOperations: failedSyncOperations,
      isSyncOnProgress: Array.from(Object.values(state.entities)).length > 1,
      isSyncDone: Array.from(Object.values(state.entities)).length == 1,
      idMappings: Array.from(Object.values(state.entities)).length == 1 ? {} : state.idMappings
    });
  }),
  on(resetOfflineOperation, (state) => {
    return offlineAdapter.removeAll({
      ...state,
      state: [],
    });
  }),

  on(updateConnectionStatus, (state, action) => {
    return {
      ...state,
      isOnline: action.isOnline,
      connectionQuality: action.connectionQuality || (action.isOnline ? 'good' : 'offline'),
      isSyncOnProgress: Array.from(Object.values(state.entities)).length > 1 && action.isOnline,
      isSyncDone: Array.from(Object.values(state.entities)).length == 0,
      idMappings: Array.from(Object.values(state.entities)).length == 0 ? {} : state.idMappings
    };
  }),
  on(addIdMapping, (state, { mapping }) => {
    return {
      ...state,
      idMappings: {
        ...state.idMappings,
        [mapping.tempId]: mapping
      }
    };
  }),

  on(clearIdMappings, (state) => {
    return {
      ...state,
      idMappings: {}
    };
  })

);
// Mapping IDs selector
export const selectIdMappings = createSelector(
  connectionFeatureSelector,
  (state) => state.idMappings
);

export const selectIdMapping = (tempId: string) => createSelector(
  selectIdMappings,
  (mappings) => mappings[tempId] || null
);

export const { selectAll } = offlineAdapter.getSelectors();
